<template>
  <TransitionRoot as="template" :show="!!props.open">
    <Dialog as="div" class="relative z-50">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-50 overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-md bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6"
            >
              <div v-if="step === 1">
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    class="text-2xl font-semibold leading-6 text-gray-900"
                  >
                    <i18n-t
                      keypath="admin.onboarding.step_one.title"
                      tag="span"
                    >
                      <template v-slot:name>
                        {{ user?.firstname }}
                      </template>
                    </i18n-t>
                  </DialogTitle>
                  <div class="mt-4">
                    <p class="text-sm font-medium text-gray-500">
                      {{ t("admin.onboarding.step_one.description") }}
                    </p>
                    <video
                      class="rounded-xs mx-auto mt-6 border-transparent focus:border-transparent focus:ring-0 outline-none"
                      :width="400"
                      controls
                      onloadstart="this.volume=0.25"
                      poster="/images/onboarding-video-thumbnail.png"
                    >
                      <source
                        src="/images/onboarding-video.mp4"
                        type="video/mp4"
                      />
                    </video>
                  </div>
                </div>
              </div>

              <div v-if="step === 2">
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    class="text-2xl font-semibold leading-6 text-gray-900"
                  >
                    {{ t("admin.onboarding.step_two.title") }}
                  </DialogTitle>
                  <div class="mt-4">
                    <p class="text-sm font-medium text-gray-500">
                      {{ t("admin.onboarding.step_two.description") }}
                    </p>
                    <img
                      :src="qrCode"
                      alt="QR Code"
                      width="245"
                      height="245"
                      class="mx-auto mt-4"
                    />
                  </div>
                </div>
              </div>

              <div v-if="step === 3">
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    class="text-2xl font-semibold leading-6 text-gray-900"
                  >
                    <i18n-t
                      keypath="admin.onboarding.step_three.title"
                      tag="span"
                    >
                      <template v-slot:name>
                        {{ user?.firstname }}
                      </template>
                    </i18n-t>
                  </DialogTitle>
                  <div class="mt-4">
                    <p class="text-sm font-medium text-gray-500">
                      {{ t("admin.onboarding.step_three.description") }}
                    </p>
                    <img
                      src="/images/onboarding-kanban.png"
                      alt="Kanban"
                      :width="350"
                      :height="225"
                      class="mx-auto mt-4 rounded-xs"
                    />
                  </div>
                </div>
              </div>

              <div
                class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3"
              >
                <secondary-button label="Skip" @click="close" />
                <primary-button
                  class="w-full sm:w-auto mt-2 sm:mt-0"
                  label="Next"
                  @click="next"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { defineProps, defineEmits, ref } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { usePage } from "@inertiajs/vue3";
import { useI18n } from "vue-i18n";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import SecondaryButton from "@/Tailwind/Components/Buttons/SecondaryButton.vue";

const emit = defineEmits(["close"]);
const props = defineProps({
  open: Boolean,
});

const { t } = useI18n();
const user = usePage().props.auth.user;
const qrCode = usePage().props.onboarding_qr_code;

const step = ref(1);

function close() {
  emit("close");
}

function next() {
  if (step.value === 3) {
    close();
    return;
  }

  step.value = step.value + 1;
}
</script>
